import React, {useEffect, useMemo, useState} from 'react';
import {StudioTextField} from '../../../base-components/StudioTextField';
import {FormattedMessage, useIntl} from 'react-intl';
import {Button, IconButton} from '@material-ui/core';
import {Clear, Delete, Edit, GetApp, RotateLeft, Settings} from '@material-ui/icons';
import FormButton from '../../../base-components/StudioButton/FormButton';
import {StudioCheckbox} from '../../../base-components/StudioCheckbox';
import {StudioSelect} from '../../../base-components/StudioSelect';
import {StudioTooltip} from '../../../base-components/StudioTooltip';
import {ACCEPTED_FILE_TYPES, TEST_PANEL_SETTINGS} from '../../../config/constants';
import {InputSource} from '../../../types/deployment/InferenceInputSource';
import InferenceAppAccordion from '../../ProdDeployView/DeploymentDetails/InferenceAppAccordion';
import {
  DeployConfigFile,
  DeployConfigState,
  getInputSourceOptions,
  resetDeploy,
  RUNTIME_MODE_OPTIONS,
  RuntimeModeType,
  setDeployConfig,
  setIsAddDeviceModalOpen,
  setIsDeleteModalOpen,
  setIsDuplicateModalOpen,
  setIsExportModalOpen,
  setPendingFiles,
  TASK_STATUS,
  TestOutputType,
  TestSourceType,
  useDeploy,
  VIDEO_SOURCES,
} from '../useDeploy';
import {
  DeploymentPostProcessingParameters,
  PreProcessingParameters,
} from './TestDeployParameters';
import {useStudioMode} from '../../StudioMode/StudioModeProvider';
import {Alert} from '@material-ui/lab';
import {DropArea} from '../../../base-components/StudioDropArea';
import {toast} from '../../../base-components/StudioToast';
import {useSelector} from 'react-redux';
import {RootState, useAppDispatch} from '../../../store';
import {useApplicationDetails} from '../../../types/project/ApplicationDetails';
import {FileExplorer} from '../FileExplorer';
import {VideoStreamSubject} from '../HlsVideoStream';
import {Application} from '../../../types/project/Application';
import {StreamingDevice} from '../../../types/deployment/StreamingDeviceResponse';
import {convertFileSize} from '../../../utils';
import {StudioTab, StudioTabPanel, StudioTabs} from '../../../base-components/StudioTab';
import {DeploySettings} from '../DeploySettings';
import ApplicationSettings from '../DeploySettings/ApplicationSettings/ApplicationSettings';
import {fetchApplicationSpaList} from '../../../store/prodDeployments/dashboard';
import getImageFileMetadata from '../../../utils/getImageFileMetadata';

type DeployConfigPanelFromProps = {
  handleSubmit: Function;
  streamSubject: VideoStreamSubject;
  fetchImageSets: Function;
  isTaskInProgress: boolean;
  applications: Application[];
  datasetExists: boolean;
  projectId: string;
  readyToRun: boolean;
  availableTestSources: {
    name: string;
    options: {
      text: string;
      value: string;
    }[];
  }[];
  defaultUserDataSource: '' | InputSource;
  datasetSourceSelected: boolean;
  isDeploying: boolean;
  videoSupported: boolean;
  imagesSupported: boolean;
  setIsPreProcessingValid: (val: boolean) => void;
  setIsPostProcessingValid: (val: boolean) => void;
  setConfirmDeleteDevice: (
    value: React.SetStateAction<{
      isOpen: boolean;
      id: string | null;
    }>
  ) => void;
  renderCancelButton: () => JSX.Element;
  calculateApplicationNames: () => Record<string, string>;
};

const DeployConfigPanelForm = ({
  handleSubmit,
  streamSubject,
  fetchImageSets,
  isTaskInProgress,
  applications,
  datasetExists,
  projectId,
  readyToRun,
  renderCancelButton,
  calculateApplicationNames,
  availableTestSources,
  defaultUserDataSource,
  datasetSourceSelected,
  setIsPreProcessingValid,
  setIsPostProcessingValid,
  setConfirmDeleteDevice,
  isDeploying,
  videoSupported,
  imagesSupported,
}: DeployConfigPanelFromProps) => {
  const intl = useIntl();
  const [
    {
      deployTask,
      inferenceTask,
      onnxTask,
      deployConfig,
      gspList,
      streamingDevices,
      imageSets,
    },
    dispatch,
  ] = useDeploy();
  const {mode} = useStudioMode();
  const appDispatch = useAppDispatch();
  const serverConfig = useSelector((state: RootState) => state.config);
  const applicationSpaLists = useSelector(
    (state: RootState) => state.deploymentDashboard.applicationSpaLists
  );
  const applicationDetails = useApplicationDetails(deployConfig.deploymentId);

  const [selectedTab, setSelectedTab] = useState(0);

  const selectedApplication = useMemo(
    () => applications.find(application => application.id === deployConfig.deploymentId),
    [applications, deployConfig.deploymentId]
  );

  const isSelectedApplicationIspOnly =
    Boolean(selectedApplication?.transformation?.id) &&
    !Boolean(selectedApplication?.model?.id);
  const isSelectedAppONNXWeb = selectedApplication?.runtimeType === 'ONNX_RUNTIME_WEB';
  const isSelectedAppCvediaRt = selectedApplication?.runtimeType === 'CVEDIA_RT';

  const tilingAvailable =
    selectedApplication?.model?.modelPurpose === 'Detection' &&
    selectedApplication?.runtimeType === 'OPENVX' &&
    (datasetSourceSelected ||
      (deployConfig.testSource === 'USER_DATA' &&
        deployConfig.inferenceSource === 'IMAGES'));
  const modelInputWidth =
    selectedApplication?.model?.frameworkParameters?.inputShape?.[2];
  const modelInputHeight =
    selectedApplication?.model?.frameworkParameters?.inputShape?.[3];
  const datasetTileWidth = selectedApplication?.dataSet?.tilingConfiguration?.width;
  const datasetTileHeight = selectedApplication?.dataSet?.tilingConfiguration?.height;

  useEffect(() => {
    if (deployConfig.deploymentId) {
      appDispatch(
        fetchApplicationSpaList({
          applicationId: deployConfig.deploymentId,
          targetType: 'TEST',
        })
      );
    }
  }, [deployConfig.deploymentId, appDispatch]);

  const ipCameras = useMemo(
    () => streamingDevices.filter(device => device.type === 'IP_CAMERA'),
    [streamingDevices]
  );

  const videoFiles = useMemo(
    () => streamingDevices.filter(device => device.type === 'FILE'),
    [streamingDevices]
  );

  const latestApplicationId = useMemo(
    () =>
      applications.sort(
        (a, b) => Date.parse(b.lastUpdated) - Date.parse(a.lastUpdated)
      )?.[0]?.id ?? '',
    [applications]
  );

  const MAX_FILE_UPLOAD_MB = useMemo(() => {
    return serverConfig?.maxVideoUploadSize
      ? convertFileSize(serverConfig?.maxVideoUploadSize, 'b', 'mb')
      : void 0;
  }, [serverConfig?.maxVideoUploadSize]);

  const [imageSetsMap, imageSetsOrder] = useMemo(() => {
    const imageSetsMap: Record<string, string> = {},
      imageSetsOrder: string[] = [];
    for (const imageSet of imageSets) {
      imageSetsMap[imageSet.id] = imageSet.name;
      imageSetsOrder.push(imageSet.id);
    }
    return [imageSetsMap, imageSetsOrder];
  }, [imageSets]);

  const inferenceSourceTypes = selectedApplication?.inputSources?.filter(
    is =>
      is !== 'DEVICE_CAMERA' && (serverConfig.webCameraSupported || is !== 'WEB_CAMERA')
  );

  const userDataSourceTypes = useMemo(
    () =>
      getInputSourceOptions(
        inferenceSourceTypes,
        (is: InputSource) =>
          ((VIDEO_SOURCES.has(is) && videoSupported) ||
            (is === 'IMAGES' && imagesSupported)) &&
          // check for webgl to only work with WEB_CAMERA
          !(
            isSelectedAppONNXWeb &&
            deployConfig.runtimeMode === 'webgl' &&
            is !== 'WEB_CAMERA'
          )
      ),
    [
      inferenceSourceTypes,
      videoSupported,
      imagesSupported,
      deployConfig.runtimeMode,
      isSelectedAppONNXWeb,
    ]
  );

  function calculateApplicationOrder(): string[] {
    return applications.sort((a, b) => b.version - a.version).map(x => x.id);
  }

  const getLabelForApplication = (applicationId: string): string => {
    switch (applicationId) {
      case latestApplicationId:
        return intl.formatMessage({id: 'test.mostRecentApplication'});
      default:
        return '';
    }
  };

  const renderTargetDeviceField = () => {
    if (isSelectedAppONNXWeb) {
      return (
        <>
          <StudioSelect
            data-testid="target-select"
            fullWidth
            label={intl.formatMessage({id: 'test.form.availableDevice'})}
            options={{}}
            disabled
            disablePlaceholder
            placeholder={intl.formatMessage({
              id: 'test.browser',
            })}
            SelectProps={{
              value: '',
            }}
          />
          <Alert severity="warning" className="dplmt-config-field__alert">
            {intl.formatMessage({id: 'test.runtime.target.warning'})}
          </Alert>
        </>
      );
    }
    if (isSelectedAppCvediaRt || !gspList || gspList.length === 0) {
      return <></>;
    }
    return (
      <StudioSelect
        data-testid="target-select"
        fullWidth
        label={intl.formatMessage({id: 'test.form.availableDevice'})}
        options={gspList.reduce(
          (options, item) => ({
            ...options,
            [item.id]: item.name,
          }),
          {}
        )}
        disabled={isTaskInProgress || !gspList.length}
        placeholder={intl.formatMessage({
          id: gspList.length ? 'test.selectDevice' : 'test.noDevices',
        })}
        disablePlaceholder
        SelectProps={{
          value: deployConfig.nodeId,
          onChange: event =>
            dispatch(setDeployConfig({nodeId: event.target.value as string})),
        }}
      />
    );
  };
  const shouldRenderTargetDeviceField = useMemo(
    () =>
      isSelectedAppONNXWeb ||
      !(isSelectedAppCvediaRt || !gspList || gspList.length === 0),
    [isSelectedAppONNXWeb, isSelectedAppCvediaRt, gspList]
  );

  const handleFileListChange = async (_newFiles: File[]) => {
    const newFiles: DeployConfigFile[] = _newFiles.map(f => ({file: f, metadata: {}}));
    const {files} = deployConfig;
    const filesMap = new Map<string, DeployConfigFile>();
    const uniqueNewFiles: DeployConfigFile[] = [];

    for (const file of files) {
      filesMap.set(file.file.name, file);
    }

    for (const newFile of newFiles) {
      const imageFile = await getImageFileMetadata(newFile.file);
      if (!filesMap.has(newFile.file.name)) {
        uniqueNewFiles.push(imageFile || newFile);
      }
    }

    if (newFiles.length > uniqueNewFiles.length) {
      dispatch(setPendingFiles(newFiles));
      dispatch(setIsDuplicateModalOpen(true));
      return;
    }

    dispatch(
      setDeployConfig({inferenceSource: 'IMAGES', files: [...files, ...uniqueNewFiles]})
    );
  };

  const handleFileListDelete = (item: File) => {
    const {files} = deployConfig;
    const newFiles = files.filter(file => file.file.name !== item.name);
    dispatch(setDeployConfig({inferenceSource: 'IMAGES', files: newFiles}));
  };

  const handleFileListClear = () => {
    dispatch(setDeployConfig({inferenceSource: 'IMAGES', files: []}));
  };

  const getFileIcon = () => {
    const {status} = inferenceTask;
    if (status === TASK_STATUS.IDLE) {
      return 'delete';
    } else if (status === TASK_STATUS.COMPLETE) {
      return 'check';
    }
    return null;
  };

  const renderFileUpload = () => {
    const label = intl.formatMessage({
      id: isSelectedApplicationIspOnly
        ? 'deployments.uploadIspOnly'
        : 'deployments.upload',
    });
    return (
      <div className="dplmt-config-field" style={{flexDirection: 'column'}}>
        <div className="textFieldlabel">{label}</div>
        {!serverConfig?.videoServerUrl && (
          <Alert severity="warning" className="dplmt-config-field__alert">
            Video inference is not available.
          </Alert>
        )}
        <DropArea
          onChange={handleFileListChange}
          disabled={isTaskInProgress}
          accept={ACCEPTED_FILE_TYPES.join(',')}
          maxSize={serverConfig?.maxVideoUploadSize || void 0}
          onDropRejected={() => {
            if (serverConfig?.maxVideoUploadSize) {
              toast.error(
                intl.formatMessage(
                  {
                    id: 'stream.videoFileUpload.error',
                  },
                  {
                    size: `${MAX_FILE_UPLOAD_MB}MB`,
                  }
                )
              );
            }
          }}
        />
        <FileExplorer
          files={deployConfig.files.map(f => f.file)}
          onDelete={handleFileListDelete}
          onClear={handleFileListClear}
          size="small"
          icon={getFileIcon()}
        />
      </div>
    );
  };

  const renderDeleteDevice = (id: string, device?: StreamingDevice | null) => {
    return (
      <>
        {device != null ? (
          <IconButton
            size="small"
            color="primary"
            onClick={() => {
              dispatch(setDeployConfig({ipCamera: device.id}));
              dispatch(setIsAddDeviceModalOpen(true));
            }}
          >
            <Edit />
          </IconButton>
        ) : null}
        <IconButton
          size="small"
          color="secondary"
          onClick={() => {
            setConfirmDeleteDevice({isOpen: true, id});
          }}
        >
          <Clear />
        </IconButton>
      </>
    );
  };

  const renderVideoFeature = () => {
    return (
      <>
        {userDataSourceTypes && Object.keys(userDataSourceTypes).length > 0 && (
          <div className="dplmt-config-field">
            <StudioSelect
              fullWidth
              label={intl.formatMessage({id: 'test.selectDataSource'})}
              options={userDataSourceTypes}
              disabled={isTaskInProgress}
              SelectProps={{
                value: deployConfig.inferenceSource,
                onChange: event =>
                  dispatch(
                    setDeployConfig({
                      ipCamera: '',
                      videoFile: '',
                      testSource: 'USER_DATA',
                      inferenceSource: event.target
                        .value as DeployConfigState['inferenceSource'],
                    })
                  ),
              }}
            />
          </div>
        )}
        {deployConfig.inferenceSource === 'IMAGES' && renderFileUpload()}
        {deployConfig.inferenceSource === 'IP_CAMERA' && (
          <div className="dplmt-config-field">
            <StudioSelect
              fullWidth
              label={intl.formatMessage({id: 'test.form.ipCamera'})}
              options={ipCameras.reduce(
                (options, item) => ({...options, [item.id]: item.name}),
                {}
              )}
              disabled={isTaskInProgress || !ipCameras.length}
              placeholder={intl.formatMessage({
                id: ipCameras.length
                  ? 'deployments.selectIpCamera'
                  : 'deployments.noIpCameras',
              })}
              disablePlaceholder
              SelectProps={{
                value: deployConfig.ipCamera,
                onChange: event => {
                  dispatch(setDeployConfig({ipCamera: event.target.value as string}));
                },
              }}
              renderEndAdornment={id =>
                renderDeleteDevice(
                  id,
                  ipCameras.find(x => x.id === id)
                )
              }
            />
            <Button
              className="dplmt-config__link"
              onClick={() => {
                dispatch(setDeployConfig({ipCamera: ''}));
                dispatch(setIsAddDeviceModalOpen(true));
              }}
              disabled={
                isDeploying ||
                isTaskInProgress ||
                deployTask.status === TASK_STATUS.IN_PROGRESS ||
                onnxTask.status === TASK_STATUS.IN_PROGRESS ||
                inferenceTask.status === TASK_STATUS.IN_PROGRESS
              }
            >
              <FormattedMessage id="deployments.addIpCamera" />
            </Button>
          </div>
        )}
        {deployConfig.inferenceSource === 'FILE' && !isSelectedApplicationIspOnly && (
          <div className="dplmt-config-field">
            <StudioSelect
              fullWidth
              label={intl.formatMessage({id: 'test.form.videoFile'})}
              options={videoFiles.reduce(
                (options, item) => ({...options, [item.id]: item.name}),
                {}
              )}
              disabled={isTaskInProgress || !videoFiles.length}
              placeholder={intl.formatMessage({
                id: videoFiles.length
                  ? 'deployments.selectVideo'
                  : 'deployments.noVideos',
              })}
              disablePlaceholder
              SelectProps={{
                value: deployConfig.videoFile,
                onChange: event => {
                  dispatch(setDeployConfig({videoFile: event.target.value as string}));
                },
              }}
              renderEndAdornment={id => renderDeleteDevice(id, null)}
            />
            <Button
              className="dplmt-config__link"
              onClick={() => dispatch(setIsAddDeviceModalOpen(true))}
            >
              <FormattedMessage id="deployments.addVideo" />
            </Button>
          </div>
        )}
      </>
    );
  };

  const renderImageSetSelector = () => {
    return (
      <div className="dplmt-config-field">
        <StudioSelect
          fullWidth
          label={intl.formatMessage({id: 'test.selectImageSet'})}
          options={imageSetsMap}
          order={imageSetsOrder}
          disabled={isTaskInProgress}
          SelectProps={{
            value: deployConfig.inferenceImageSetId,
            onChange: event =>
              dispatch(
                setDeployConfig({
                  testSource: 'IMAGE_SETS',
                  inferenceImageSetId: event.target.value as string,
                })
              ),
          }}
        />
      </div>
    );
  };

  return (
    <>
      <StudioTabs
        className="prod-deploy-details__tabs"
        value={selectedTab}
        onChange={(_event, value) => {
          setSelectedTab(value);
        }}
      >
        <StudioTab
          disabled={Boolean(isDeploying || isTaskInProgress)}
          label={intl.formatMessage({id: 'test.editTest'})}
          className="prod-deploy-details__tab"
          icon={<Edit />}
        />
        <StudioTab
          disabled={Boolean(isDeploying || isTaskInProgress)}
          label={intl.formatMessage({id: 'prodDeployment.tabSettings'})}
          className="prod-deploy-details__tab"
          icon={<Settings />}
        />
      </StudioTabs>
      <StudioTabPanel className="dplmt-tab-panel" value={selectedTab} index={0}>
        <form
          className="dplmt-form"
          onSubmit={event => {
            event.preventDefault();
            if (!isDeploying || !isTaskInProgress) {
              handleSubmit(deployConfig, streamSubject, fetchImageSets);
            }
          }}
        >
          <div className="dplmt-scrollable-container">
            <div className="dplmt-config-field-container">
              <div className="dplmt-config-field">
                <StudioTextField
                  value={deployConfig.name}
                  required
                  fullWidth
                  label={
                    <React.Fragment>
                      {intl.formatMessage({id: 'test.form.name'})}
                      <StudioTooltip
                        title={intl.formatMessage({id: 'deployments.resetForm'})}
                      >
                        <span>
                          <IconButton
                            className="dplmt-download"
                            onClick={() => dispatch(resetDeploy())}
                            disabled={isTaskInProgress}
                          >
                            <RotateLeft />
                          </IconButton>
                        </span>
                      </StudioTooltip>
                    </React.Fragment>
                  }
                  data-testid="test-name-field"
                  id="test-name-field"
                  disabled={isTaskInProgress}
                  onChange={event => {
                    dispatch(setDeployConfig({name: event.target.value}));
                  }}
                  inputProps={{
                    maxLength: TEST_PANEL_SETTINGS.name.maxLength,
                  }}
                  InputLabelProps={{
                    required: false,
                  }}
                />
              </div>
              <div className="dplmt-config-field">
                <StudioTextField
                  fullWidth
                  disabled={isTaskInProgress}
                  value={deployConfig.description}
                  label={intl.formatMessage({id: 'form.description'})}
                  multiline={TEST_PANEL_SETTINGS.description.multiline}
                  inputProps={{
                    className: TEST_PANEL_SETTINGS.description.className,
                    maxLength: TEST_PANEL_SETTINGS.description.maxLength,
                  }}
                  onChange={event => {
                    dispatch(setDeployConfig({description: event.target.value}));
                  }}
                />
              </div>
              <div className="dplmt-config-field dplmt-download-field">
                <StudioSelect
                  fullWidth
                  label={intl.formatMessage({id: 'test.availableApplication'})}
                  getAuxiliaryLabel={(optionId: string) =>
                    getLabelForApplication(optionId)
                  }
                  additionalLabel={
                    <div className="dplmt-app-actions">
                      <StudioTooltip
                        title={intl.formatMessage({id: 'test.exportTooltip'})}
                      >
                        <span>
                          <IconButton
                            className="dplmt-download"
                            onClick={() => dispatch(setIsExportModalOpen(true))}
                            disabled={!deployConfig.deploymentId || isTaskInProgress}
                            data-testid="export-button"
                          >
                            <GetApp />
                          </IconButton>
                        </span>
                      </StudioTooltip>
                      {mode !== 'LITE' && (
                        <StudioTooltip
                          title={intl.formatMessage({id: 'test.deleteApplication'})}
                        >
                          <span>
                            <IconButton
                              className="dplmt-delete-app"
                              onClick={() => dispatch(setIsDeleteModalOpen(true))}
                              disabled={!deployConfig.deploymentId || isTaskInProgress}
                              data-testid="delete-app-button"
                            >
                              <Delete />
                            </IconButton>
                          </span>
                        </StudioTooltip>
                      )}
                    </div>
                  }
                  data-testid="application-select"
                  id="application-select"
                  options={calculateApplicationNames()}
                  order={calculateApplicationOrder()}
                  disabled={isTaskInProgress || !applications.length}
                  placeholder={intl.formatMessage({
                    id: applications.length
                      ? 'test.selectApplication'
                      : 'test.noApplications',
                  })}
                  disablePlaceholder
                  SelectProps={{
                    value: deployConfig.deploymentId,
                    onChange: event => {
                      const newApplicationId = event.target.value as string;
                      const newApplication = applications.find(
                        application => application.id === newApplicationId
                      );
                      const isNewAppONNXWeb =
                        newApplication?.runtimeType === 'ONNX_RUNTIME_WEB';
                      dispatch(
                        setDeployConfig({
                          deploymentId: newApplicationId,
                          isONNXRuntimeWeb: isNewAppONNXWeb,
                          testSource: isNewAppONNXWeb ? 'USER_DATA' : '',
                          outputTypes: [],
                          files: [],
                          environment: newApplication?.environment || {},
                        })
                      );
                    },
                  }}
                />
              </div>
              {shouldRenderTargetDeviceField && (
                <div className="dplmt-config-field">{renderTargetDeviceField()}</div>
              )}
              {isSelectedAppONNXWeb && (
                <div className="dplmt-config-field">
                  <StudioSelect
                    required
                    id="runtime-mode"
                    fullWidth
                    disabled={isTaskInProgress}
                    label={intl.formatMessage({id: 'test.runtime.label'})}
                    options={RUNTIME_MODE_OPTIONS}
                    SelectProps={{
                      value: deployConfig.runtimeMode,
                      onChange: event =>
                        dispatch(
                          setDeployConfig({
                            runtimeMode: event.target.value as RuntimeModeType,
                          })
                        ),
                    }}
                    tooltip={intl.formatMessage({id: 'test.runtime.tooltip'})}
                    tooltipPlacement="top"
                  />
                </div>
              )}
              {selectedApplication &&
                (isSelectedAppCvediaRt || deployConfig.nodeId) &&
                !isSelectedAppONNXWeb && (
                  <div className="dplmt-config-field">
                    <StudioSelect
                      data-testid="test-source"
                      id="test-source"
                      fullWidth
                      label={intl.formatMessage({id: 'test.form.testSource'})}
                      groupedOptions={availableTestSources}
                      disabled={isTaskInProgress}
                      placeholder={intl.formatMessage({
                        id: 'test.form.testSourcePlaceholder',
                      })}
                      disablePlaceholder
                      SelectProps={{
                        value: deployConfig.testSource,
                        onChange: event => {
                          const isApp = event.target.value === 'APP_INPUT';
                          const isTxt = event.target.value === 'TEXT_INPUT';
                          const isUserData = event.target.value === 'USER_DATA';
                          var userDataSource = defaultUserDataSource;
                          if (isUserData && userDataSourceTypes && userDataSource) {
                            if (!userDataSourceTypes.hasOwnProperty(userDataSource)) {
                              const keys = Object.keys(userDataSourceTypes);
                              if (keys.length > 0) {
                                userDataSource = keys[0] as InputSource;
                              }
                            }
                          }
                          return (
                            event.target.value != null &&
                            dispatch(
                              setDeployConfig({
                                testSource: event.target.value as TestSourceType,
                                outputTypes: isUserData ? [] : deployConfig.outputTypes,
                                inferenceSource:
                                  isApp || isTxt
                                    ? (event.target.value as InputSource)
                                    : userDataSource,
                                files: [],
                              })
                            )
                          );
                        },
                      }}
                    />
                  </div>
                )}
              {datasetExists && datasetSourceSelected && !isSelectedAppONNXWeb && (
                <div className="dplmt-config-field">
                  <StudioSelect
                    data-testid="test-output-type"
                    id="test-output-type"
                    fullWidth
                    disabled={isTaskInProgress}
                    label={intl.formatMessage({
                      id: 'test.outputType',
                    })}
                    required
                    options={{
                      modelStatistics: intl.formatMessage({
                        id: 'test.validationRun',
                      }),
                      sampleInference: intl.formatMessage({
                        id: 'test.inferenceRun',
                      }),
                    }}
                    disabledOptions={{
                      modelStatistics: isSelectedApplicationIspOnly,
                    }}
                    placeholder={intl.formatMessage({
                      id: 'form.selectOneOrMore',
                    })}
                    SelectProps={{
                      value: deployConfig.outputTypes,
                      multiple: true,
                      onChange: event =>
                        dispatch(
                          setDeployConfig({
                            outputTypes: event.target.value as TestOutputType[],
                          })
                        ),
                    }}
                    onChipDelete={value =>
                      dispatch(
                        setDeployConfig({
                          outputTypes: deployConfig.outputTypes.filter(
                            type => type !== value
                          ),
                        })
                      )
                    }
                    tooltip={
                      intl.formatMessage(
                        {
                          id: 'test.outputTypeTooltip',
                        },
                        {
                          br: (
                            <>
                              <br /> <br />
                            </>
                          ),
                        }
                      ) as string
                    }
                    tooltipPlacement="top"
                  />
                </div>
              )}
              {(deployConfig.testSource === 'USER_DATA' || isSelectedAppONNXWeb) && (
                <>
                  {!serverConfig?.videoServerUrl && renderFileUpload()}
                  {serverConfig?.videoServerUrl && renderVideoFeature()}
                </>
              )}
              {deployConfig.testSource === 'IMAGE_SETS' && renderImageSetSelector()}
              {tilingAvailable && (
                <div className="dplmt-config-field">
                  <StudioCheckbox
                    name={intl.formatMessage({id: 'test.form.tiling'})}
                    label={intl.formatMessage({id: 'test.form.tiling'})}
                    checked={deployConfig.useTiling}
                    disabled={isTaskInProgress}
                    onChange={(_event, checked) => {
                      dispatch(
                        setDeployConfig({
                          useTiling: checked,
                        })
                      );
                    }}
                  />
                  {deployConfig.useTiling && (
                    <div>
                      {modelInputWidth && modelInputHeight && (
                        <FormButton
                          buttonRole="naked"
                          value={intl.formatMessage({id: 'test.form.tiling.set.model'})}
                          onClick={() => {
                            dispatch(
                              setDeployConfig({
                                tilingWidth: modelInputWidth,
                                tilingHeight: modelInputHeight,
                              })
                            );
                          }}
                        />
                      )}
                      {datasetTileWidth && datasetTileHeight && (
                        <FormButton
                          buttonRole="naked"
                          value={intl.formatMessage({
                            id: 'test.form.tiling.set.dataset',
                          })}
                          onClick={() => {
                            dispatch(
                              setDeployConfig({
                                tilingWidth: datasetTileWidth,
                                tilingHeight: datasetTileHeight,
                              })
                            );
                          }}
                        />
                      )}
                      <StudioTextField
                        value={deployConfig.tilingWidth}
                        label={intl.formatMessage({id: 'test.form.tiling.width'})}
                        type="number"
                        disabled={isTaskInProgress}
                        onChange={event => {
                          dispatch(
                            setDeployConfig({
                              tilingWidth: parseInt(event.target.value, 10),
                            })
                          );
                        }}
                      />
                      <StudioTextField
                        value={deployConfig.tilingHeight}
                        label={intl.formatMessage({id: 'test.form.tiling.height'})}
                        type="number"
                        disabled={isTaskInProgress}
                        onChange={event => {
                          dispatch(
                            setDeployConfig({
                              tilingHeight: parseInt(event.target.value, 10),
                            })
                          );
                        }}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
            {(isSelectedAppONNXWeb ||
              applicationDetails?.supportsSinglePageApplications) &&
              !isSelectedApplicationIspOnly &&
              mode !== 'LITE' && (
                <InferenceAppAccordion
                  singlePageApplications={applicationSpaLists || []}
                  applicationDetails={applicationDetails}
                  projectId={projectId}
                  applicationId={selectedApplication?.id}
                  deploymentId={deployConfig.deploymentId}
                  fromTest={true}
                />
              )}
          </div>
          <div className="dplmt-config-action-buttons">
            {deployTask.status !== TASK_STATUS.IN_PROGRESS &&
            onnxTask.status !== TASK_STATUS.IN_PROGRESS ? (
              <Button
                variant="contained"
                fullWidth
                size="large"
                disabled={!readyToRun || isTaskInProgress}
                color="primary"
                type="submit"
              >
                <FormattedMessage id="deployments.runTest" />
              </Button>
            ) : (
              renderCancelButton()
            )}
          </div>
        </form>
      </StudioTabPanel>
      <StudioTabPanel
        className="dplmt-tab-panel dplmt-settings-tab"
        value={selectedTab}
        index={1}
      >
        {Boolean(deployConfig.deploymentId) && (
          <PreProcessingParameters
            projectId={projectId}
            applicationId={deployConfig.deploymentId}
            renderField={(field, _) => <>{field}</>}
            titleMessageId="test.form.preprocessing"
            wrapperClassName="dplmt-config__accordion"
            detailsClassName="deploy-settings__body_container"
            className="dplmt-config__accordion__container"
            onValidityChange={isValid => setIsPreProcessingValid(isValid)}
            onChange={newParams =>
              dispatch(setDeployConfig({preProcessorParams: newParams}))
            }
            persistChanges={true}
            disabledForm={isTaskInProgress}
          />
        )}
        {Boolean(deployConfig.deploymentId) && (
          <DeploymentPostProcessingParameters
            projectId={projectId}
            applicationId={deployConfig.deploymentId}
            renderField={(field, _) => <>{field}</>}
            titleMessageId="test.form.postprocessing"
            wrapperClassName="dplmt-config__accordion"
            detailsClassName="deploy-settings__body_container"
            className="dplmt-config__accordion__container"
            onValidityChange={isValid => setIsPostProcessingValid(isValid)}
            onChange={newParams =>
              dispatch(setDeployConfig({postProcessorParams: newParams}))
            }
            persistChanges={true}
            disabledForm={isTaskInProgress}
          />
        )}
        <ApplicationSettings
          modelPurpose={selectedApplication?.model?.modelPurpose}
          isAnalytics={selectedApplication?.analyticsApplication}
        />
        {/* this would be enabled in future versions not available in 1.5.4 */}
        {/* <PostProcessorParametersAccordion
          modelPurpose={selectedApplication?.model?.modelPurpose}
        /> */}
        <DeploySettings />
      </StudioTabPanel>
    </>
  );
};

export default DeployConfigPanelForm;
